<template>
  <v-container
    :class="[initiatedPayment ? 'cursor-wait' : '', '']"
    class="remove-mobile-container-padding"
  >
    <div v-if="personId || shoppingBasketId">
      <div :class="initiatedPayment ? 'disable-interaction' : ''">
        <div class="manual-restire-mobile-container-padding">
          <section
            v-if="feedbackAlert.show"
            role="alert"
            :class="[feedbackAlert.color, 'pa-5 elevation-1 my-5']"
            style="font-size: 16px"
          >
            {{ feedbackAlert.message }}
          </section>
          <h2 class="bold my-2">
            {{
              $t(
                "people:ShoppingBasketPage.title",
                "Shopping basket and payments history",
                "Title of the page"
              )
            }}
          </h2>
          <AppTabsCustom v-model="tabSelected">
            <v-tab
              class="larger-tab"
              href="#basket"
              @click="
                evt => {
                  refetch();
                  historyTabClick(evt);
                }
              "
            >
              {{
                $t(
                  "people:ShoppingBasketPage.ShoppingBasketTab.title",
                  "Shopping basket",
                  "Title of shopping basket tab"
                )
              }}
            </v-tab>
            <v-tab
              v-if="showDebtsTab"
              class="larger-tab"
              href="#personDebtBalances"
              @click="
                evt => {
                  historyTabClick(evt);
                  refetchDebts();
                }
              "
            >
              {{ $t("people:ShoppingBasketPage.debtsTab.heading", "DEBTS") }}
            </v-tab>
            <v-tab
              v-if="showPaymentsHistory"
              class="larger-tab"
              href="#history"
              @click="
                evt => {
                  historyTabClick(evt);
                  refetchHistory = !refetchHistory;
                }
              "
            >
              {{
                $t(
                  "people:ShoppingBasketPage.PaymentsHistoryTab.title",
                  "Payments history",
                  "Title of payments history tab"
                )
              }}
            </v-tab>
            <v-tab
              v-if="$allowedMutation(person.allowedFields, 'payouts')"
              class="larger-tab"
              href="#payouts"
              @click="historyTabClick"
            >
              <AppAlertIcon v-if="pendingPayouts" />
              {{
                $t(
                  "people:ShoppingBasketPage.prizesRefundsTab.heading",
                  "PRIZES | REFUNDS"
                )
              }}
            </v-tab>
            <v-tab
              v-if="personId && person.id"
              class="larger-tab"
              href="#balance"
              @click="
                evt => {
                  historyTabClick(evt);
                  refetchHistory = !refetchHistory;
                }
              "
            >
              {{
                $t(
                  "people:ShoppingBasketPage.balancesTab.title",
                  "Event finances"
                )
              }}
            </v-tab>

            <v-tab
              v-if="personId && person.reports.length > 0"
              class="larger-tab"
              href="#reports"
              @click="historyTabClick"
            >
              {{ $t("people:ShoppingBasketPage.reportsTab.title", "Reports") }}
            </v-tab>
          </AppTabsCustom>
        </div>
        <v-tabs-items ref="validTabs" v-model="tabSelected" touchless>
          <v-tab-item value="basket">
            <div class="manual-restire-mobile-container-padding">
              <AppLoadingPage
                v-if="
                  $apollo.queries.personShoppingBasket.loading ||
                  $apollo.queries.shoppingBasket.loading
                "
              />
              <template v-else>
                <AppAlertNotice
                  v-if="
                    person.shoppingBasketAlertMessages &&
                    person.shoppingBasketAlertMessages.length > 0
                  "
                >
                  <template #default>
                    <div
                      v-for="(msg, i) in person.shoppingBasketAlertMessages"
                      :key="i"
                    >
                      {{ displayText(msg) }}
                    </div>
                  </template>
                </AppAlertNotice>
                <OrdersBasket
                  :payables="payables"
                  :person="person"
                  :shopping-basket-id="shoppingBasketId"
                  @initiatedPayment="$event => (initiatedPayment = $event)"
                  @refetch="() => refetch()"
                  @toHistory="() => toHistory()"
                />
              </template>
            </div>
          </v-tab-item>

          <v-tab-item v-if="showPaymentsHistory" value="history" eager>
            <OrdersHistory
              ref="ordersHistory"
              :person-id="personId"
              :shopping-basket-id="shoppingBasketId"
              :refetch-history="refetchHistory"
              @refetch="refetch(true)"
            />
          </v-tab-item>

          <v-tab-item v-if="showDebtsTab" value="personDebtBalances" eager>
            <OrderDebts
              ref="debtsTab"
              :person="person"
              @refetch="() => refetchDebts()"
            />
          </v-tab-item>

          <v-tab-item value="payouts">
            <PayoutsHistory :person-id="personId" />
          </v-tab-item>

          <v-tab-item v-if="personId && person.id" value="balance">
            <div class="manual-restire-mobile-container-padding">
              <OrdersBalance
                :person="person"
                :refetch-balance="refetchBalance"
              />
            </div>
          </v-tab-item>
          <v-tab-item
            v-if="personId && person.reports.length > 0"
            value="reports"
          >
            <OrderReports :person="person" />
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>
  </v-container>
</template>
<script>
import { defineComponent } from "vue";
import { loadStripe } from "@stripe/stripe-js";
import gql from "fraql";
import AppTabsCustom from "@/components/elements/AppTabsCustom.vue";
import { orderBy } from "lodash-es";
import { useTabHistory } from "@composables/tab_history";

import AppShowHorseWithDetails from "@AppShows/horse/AppShowHorseWithDetails.vue";

import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import AppAlertIcon from "@elements/icons/AppAlertIcon.vue";

import {
  useDisplayText,
  DisplayTextFragments
} from "@composables/display_text";

import OrdersBasket from "./OrdersBasket.vue";
import OrdersHistory from "./OrdersHistory.vue";
import OrdersBalance from "./OrdersBalance.vue";
import OrderReports from "./OrderReports.vue";
import PayoutsHistory from "./PayoutsHistory.vue";
import OrderDebts from "./debts/OrderDebts.vue";

import FipaListener from "@/utils/listenerUtility";

export default defineComponent({
  name: "AppOrders",
  components: {
    AppLoadingPage,
    OrdersBasket,
    OrdersHistory,
    OrdersBalance,
    AppAlertNotice,
    AppTabsCustom,
    OrderReports,
    PayoutsHistory,
    AppAlertIcon,
    OrderDebts
  },
  provide() {
    return {
      shoppingBasketId: this.shoppingBasketId,
      refetchBasket: () => {
        if (this.personId) {
          this.$apollo.queries.personShoppingBasket.refetch();
        } else {
          this.$apollo.queries.shoppingBasket.refetch();
        }
      }
    };
  },
  props: {
    personId: {
      type: String,
      default: null
    },
    shoppingBasketId: {
      type: String,
      default: null
    }
  },
  setup() {
    const { tabSelected, historyTabClick, setHistoryTab } =
      useTabHistory("basket");

    const { displayText } = useDisplayText();
    return {
      tabSelected,
      historyTabClick,
      setHistoryTab,
      displayText
    };
  },
  data() {
    return {
      feedbackAlert: {
        alert: false,
        color: null,
        message: null
      },
      initiatedPayment: false,
      alert: {
        show: false,
        color: null
      },
      refetchHistory: false,
      refetchBalance: false,
      payables: [],
      pendingPayouts: false,
      person: {
        id: null,
        name: null,
        reports: [],
        personDebtBalances: [],
        allowedFields: [],
        allowedMutations: []
      }
    };
  },
  computed: {
    showPaymentsHistory() {
      return this.$allowedMutation(this.person.allowedFields, "payments");
    },
    showDebtsTab() {
      return (
        this.person.personDebtBalances.length > 0 ||
        this.$allowedMutation(
          this.person.allowedMutations,
          "personDebtBalanceCreateOrUpdate"
        )
      );
    }
  },
  async mounted() {
    // Retrieve the "payment_intent_client_secret" query parameter appended to
    // your return_url by Stripe.js
    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (clientSecret && window.Stripe && typeof window.Stripe === "function") {
      // Clear out parameters
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        hash: window.location.hash
      });

      // Initialize Stripe.js using your publishable key
      const stripe = await loadStripe(
        process.env.VUE_APP_PAYMENT_STRIPE_CONNECT_PUBLISABLE_KEY
      );

      // Retrieve the PaymentIntent
      stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
        // Inspect the PaymentIntent `status` to indicate the status of the payment
        // to your customer.
        //
        // Some payment methods will [immediately succeed or fail][0] upon
        // confirmation, while others will first enter a `processing` state.
        //
        // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
        switch (paymentIntent.status) {
          case "succeeded":
          case "processing":
            /* this.feedbackAlert.message = "";
          this.feedbackAlert.color = "primary-light";
          this.feedbackAlert.show = true;*/
            this.tabSelected = "history";
            break;

          default:
            this.feedbackAlert.message = `${this.$t(
              "shoppingBasket.feedback.payment_failed",
              "Payment failed, please try again or try another method"
            )}`;
            this.feedbackAlert.color = "alert";
            this.feedbackAlert.show = true;
            break;
        }
      });

      return;
    }

    // Old code that might be still working
    const params = new URLSearchParams(window.location.search);
    if (params.has("feedback_msg_type")) {
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        hash: window.location.hash
      });

      const type = params.get("feedback_msg_type");
      const messageId = params.get("feedback_msg");
      this.feedbackAlert.message = this.$t(messageId);
      if (["success", "error", "warning", "info"].includes(type)) {
        switch (type) {
          case "success":
            this.feedbackAlert.color = "primary-light";
            break;
          case "info":
            this.feedbackAlert.color = "primary-light";
            break;
          case "error":
            this.feedbackAlert.color = "alert";
            break;
          case "warning":
            this.feedbackAlert.color = "alert";
            break;
        }
        this.feedbackAlert.show = true;
      }
    }
  },
  apollo: {
    personShoppingBasket: {
      query: gql`
        query personShoppingBasket($id: ID) {
          person(id: $id) {
            id
            name
            allowedFields
            allowedMutations

            shoppingBasketAlertMessages {
              ${DisplayTextFragments.translation}
            }

            personDebtBalances {
              id
              ${OrderDebts.fragments.personDebtBalanceFields}
            }

            payouts(limit: 1, statuses: ["PENDING"]) {
              id
            }

            reports(placement: "PERSON_PROFILE_SHOPPINGBASKET") {
              ${OrderReports.fragments.reports}
            }

            payables {
              id
              availableRefundsCents
              entityType
              ${OrdersBasket.fragments.personPayableGroupFields}
              singlePaymentForWholeGroup
              mandatory
              disabled
              disableReason {
                ${DisplayTextFragments.translation}
              }
              invoice {
                id
                status
                ${OrdersBasket.fragments.invoiceFields}
              }
              payment {
                id
                ${OrdersBasket.fragments.paymentFields}
              }
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
              payableSections {
                id
                rank
                entityType
                product {
                  id
                  ${OrdersBasket.fragments.payabeSectionProductFields}
                }
                registration {
                  id
                  ${OrdersBasket.fragments.registrationFields}
                  athletes {
                    id
                    firstName
                    lastName
                  }
                  horses {
                    id
                    name
                  }
                }
                payableItems {
                  id
                  rank
                  mandatory
                  entityType
                  amountCents
                  paymentDeadline
                  reasonText
                  paymentNotAllowedReasons {
                    ${DisplayTextFragments.translation}
                  }
                  paymentInProcess
                  payable {
                    id
                    ${OrdersBasket.fragments.payableFields}
                  }
                  invoiceLine {
                    id
                    ${OrdersBasket.fragments.invoiceLineFields}
                  }
                  organisation {
                    id
                    name
                    shortName
                  }
                  registrationEventProduct {
                    id
                    quantity
                    eventProductId
                    eventProduct {
                      id
                      name
                    }
                  }
                  horse {
                    id
                    ${AppShowHorseWithDetails.fragments.horse}
                  }
                  horseApplication {
                    id
                    productParams
                    horseBirthName
                    horse {
                      id
                      name
                    }
                  }
                  entry {
                    id
                    ${OrdersBasket.fragments.entryFields}
                    competition {
                      id
                      code
                      name
                    }
                  }
                }
              }
            }
          }
        }
      `,
      manual: true,
      variables() {
        return {
          id: this.personId
        };
      },
      skip() {
        return !this.personId;
      },
      result({ loading, data }) {
        if (loading || !data || !data.person) return;
        const { person } = data;
        if (
          !this.$allowedMutation(person.allowedMutations, "paymentInitiate")
        ) {
          this.$router.push({ name: "403" });
          return;
        }

        this.payables = this.orderPayables(
          JSON.parse(JSON.stringify(person.payables))
        );

        this.pendingPayouts = person.payouts.length > 0;

        FipaListener.emit("breadcrumbPageName", {
          name: person.name,
          id: person.id,
          breadcrumbName: "personName"
        });

        this.$set(this, "person", {
          id: person.id,
          name: person.name,
          personDebtBalances: person.personDebtBalances,
          shoppingBasketAlertMessages: person.shoppingBasketAlertMessages,
          reports: person.reports,
          allowedFields: person.allowedFields,
          allowedMutations: person.allowedMutations
        });

        this.$nextTick(() => {
          const params = new URLSearchParams(location.search);

          if (this.showPaymentsHistory && params.has("historyForeignFilters")) {
            const historyForeignFilters = JSON.parse(
              params.get("historyForeignFilters")
            );

            this.$refs.ordersHistory.filters.dateFrom =
              historyForeignFilters.dateFrom ?? null;
            this.$refs.ordersHistory.filters.dateUntil =
              historyForeignFilters.dateUntil ?? null;
            this.$refs.ordersHistory.filters.search =
              historyForeignFilters.search ?? null;
          }

          this.tabSelected = [
            "#basket",
            "#history",
            "#personDebtBalances",
            "#payouts",
            "#balance",
            "#reports"
          ].includes(this.$route.hash)
            ? this.$route.hash.split("#")[1]
            : "basket";
        });

        FipaListener.emit("emitRefreshBasket");
      },
      fetchPolicy: "network-only"
    },
    shoppingBasket: {
      query: gql`
        query shoppingBasket($id: ID) {
          shoppingBasket(id: $id) {
            id
            payableGroups {
              id
              ${OrdersBasket.fragments.personPayableGroupFields}
              availableRefundsCents
              entityType
              mandatory              
              disabled
              disableReason {
                ${DisplayTextFragments.translation}
              }
              payment {
                id
                ${OrdersBasket.fragments.paymentFields}
              }
              payableSections {
                id
                rank
                entityType
                product {
                  id
                  codename
                  name
                  systemTags
                }
                registration {
                  id
                  athletes {
                    id
                    firstName
                    lastName
                  }
                  horses {
                    id
                    name
                  }
                }
                payableItems {
                  id
                  rank
                  mandatory
                  entityType
                  amountCents
                  paymentDeadline
                  reasonText
                  paymentInProcess
                  payable {
                    id
                    productCodename
                    productParams
                    ${OrdersBasket.fragments.payableFields}
                    product {
                      id
                      name
                    }
                    year                    
                    allowedMutations
                  }
                  registrationEventProduct {
                    id
                    quantity
                    eventProduct {
                      id
                      name
                      priceCents
                      bookingFeeCents
                      paymentStructure
                    }
                  }
                  horse {
                    id
                    ${AppShowHorseWithDetails.fragments.horse}
                  }
                  horseApplication {
                    id
                    productParams
                    horseBirthName
                    horse {
                      id
                      name
                    }
                  }
                  entry {
                    id
                    ${OrdersBasket.fragments.entryFields}
                    competition {
                      id
                      code
                      name
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return {
          id: this.shoppingBasketId
        };
      },
      manual: true,
      skip() {
        return !this.shoppingBasketId;
      },
      result({ loading, data }) {
        if (loading || !data || !data.shoppingBasket) return;
        this.payables = this.orderPayables(
          JSON.parse(JSON.stringify(data.shoppingBasket.payableGroups))
        );

        this.$set(this.person, "allowedFields", ["payments"]);

        FipaListener.emit("breadcrumbPageName", {
          name: "Anonymous",
          id: null,
          breadcrumbName: "personName"
        });
      },
      fetchPolicy: "network-only"
    }
  },
  methods: {
    async refetchDebts() {
      //if (!this.$refs.debtsTab) return; // not yet rendered
      if (this.$refs.debtsTab.refetchUnderway) return;
      this.$refs.debtsTab.refetchUnderway = true;

      try {
        await this.$apollo.query({
          query: gql`
            query refetchPersonDebtBalances($id: ID) {
              person(id: $id) {
                id
                personDebtBalances {
                  id
                  ${OrderDebts.fragments.personDebtBalanceFields}
                }
              }
            }
          `,
          variables: {
            id: this.personId
          },
          fetchPolicy: "network-only"
        });
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.$refs.debtsTab.refetchUnderway = false;
      }
    },
    orderPayables(payables) {
      const { invoiceless, invoices } = payables.reduce(
        (obj, payable) => {
          if (payable?.invoice?.status === "DRAFT") {
            obj.invoices.push(payable);
          } else {
            obj.invoiceless.push(payable);
          }
          return obj;
        },
        {
          invoiceless: [],
          invoices: []
        }
      );

      return [
        ...orderBy(invoiceless, ["disabled", "mandatory"], ["desc", "desc"]),
        ...invoices
      ];
    },
    toHistory() {
      this.$apollo.queries.personShoppingBasket.refetch();

      this.$nextTick(() => {
        this.refetchHistory = !this.refetchHistory;
        window.requestAnimationFrame(() => {
          const doc = document.querySelector("#ordersHistory");
          if (doc) doc.scrollIntoView(true);
          this.setHistoryTab("#history");
        });
      });
    },
    refetch(fromHistory) {
      if (this.person.id) {
        this.$apollo.queries.personShoppingBasket.refetch();
      } else {
        this.$apollo.queries.shoppingBasket.refetch();
      }

      // Do not refetch if this method is triggered trough emit from OrdersHistory
      if (!fromHistory) {
        this.refetchHistory = !this.refetchHistory;
      }
    }
  }
});
</script>
<style lang="scss">
.larger-tab {
  padding: 0 24px !important;
  text-transform: uppercase !important;
  .v-tabs__item {
    padding: 0 !important;
    font-size: 18px !important;
    font-weight: bold !important;
    text-transform: normal !important;
  }

  @media (max-width: 768px) {
    padding: 0 14px !important;
    font-size: 12px !important;
    font-weight: bold !important;
    text-transform: capitalize !important;
    .v-tabs__item {
      padding: 0 !important;
      font-size: 15px !important;
      font-weight: bold !important;
      text-transform: normal !important;
    }
  }
}
</style>
